<template>
  <section id="state-overall">
    <figure class="chart">
      <div class="row clearfix">
        <h1>Revenue and Attendance by State</h1>

        <div class="sub">
          <span class="periscope"></span>
          <span class="boaty"></span>
        </div>

        <ul class="switches">
          <li v-for="(year, index) in years" :key="index">
            <a href="javascript:;" :class="{active: activeYear==year}" @click="activeYear=year">{{ year }}</a>
          </li>
        </ul>

        <div class="chart-wrapper">
          <highcharts id="state-overall-map" class="mapcontainer" :constructorType="'mapChart'" :options="revAttByState" ></highcharts>
          <div class="nb">NB: Numbers may not total 100.0% due to rounding.</div>
        </div>
      </div>
    </figure>
    <article>
      <div class="row clearfix">
        <h1>Overall Results</h1>

        <div class="sixcol">
          <p><strong>NSW</strong> experienced a y-o-y decline in revenue between 2018 and 2020 (by 11.5% to $666.7m in 2019 and 72.6% to $182.5m in 2020) and a y-o-y decline in attendance (by 13.0% to 7.2m in 2019 and 72.6% to 2.0m in 2020).</p>
          <p>In 2019, the decline in revenue was driven by a significant decline in Festivals (Multi-Category) (40.4%), Children&rsquo;s/Family (37.3%), Comedy (27.9%) and Musical Theatre (26.2%). This was partially offset by a growth in Circus and Physical Theatre (222.9%), Special Events (157.0%) and Festivals (Contemporary Music) (43.1%).</p>
          <ul>
            <li>Festivals (Multi-Category), Comedy and Musical Theatre revenue declined, primarily driven by a decline in both attendance and average ticket price. Children&rsquo;s/Family revenue declined, primarily driven by a decline in attendance. This was partially offset by a slight increase in average ticket price.</li>
            <li>Circus and Physical Theatre revenue increased due to an increase in attendance and the average ticket price, which was driven by Cirque du Soleil tours. Special Events revenue increased due to a significant increase in attendance, partially offset by the decline in average ticket price. Festivals (Contemporary Music) revenue increased due to an increase in attendance and average ticket price.</li>
          </ul>
          <p>In 2020, all categories experienced a significant decline in revenue and attendance due to COVID-19 restrictions.&nbsp;</p>
          <ul>
            <li>The decline in revenue was primarily driven by a significant decline in Circus and Physical Theatre (97.4%), Special Events (95.8%), Ballet and Dance (87.9%) and Classical Music (87.5%).</li>
            <li>Circus and Physical Theatre and Classical Music revenue decline was driven by a decline in attendance, partially offset by the increase in average ticket price. Special Events and Ballet and Dance revenue decline was driven by a decline in attendance and average ticket price.&nbsp;</li>
          </ul>
          <p><strong>Queensland </strong>experienced a y-o-y decline in revenue between 2018 and 2020 (by 9.8% to $286.2m in 2019 and 59.2% to $116.9m in 2020) and a y-o-y decline in attendance (by 9.5% to 3.5m in 2019 and 63.7% to 1.3m in 2020).</p>
          <p>In 2019, the decline in revenue was primarily attributable to Special Events (93.6%), Comedy (37.3%) and Festivals (Multi-Category) (25.9%). The decline was partially offset by growth in revenue in Festivals (Contemporary Music) (85.1%) and Ballet and Dance (36.3%).&nbsp;</p>
          <ul>
            <li>Top events included <em>The Book of Mormon</em>, CMC Rocks QLD and performances from Eminem and U2.</li>
          </ul>
          <p>In 2020, the decline in revenue was primarily due to Ballet and Dance (90.7%), Festivals (Multi-Category) (87.5%), Musical Theatre (85.1%) and Opera (85.1%). The decline was partially offset by a growth in revenue in Circus and Physical Theatre (216.7%) and Special Events (74.4%).</p>
          <ul>
            <li>Top events included <em>The Book of Mormon</em> and performances from Queen + Adam Lambert and Elton John.</li>
          </ul>
          <p><strong>Victoria</strong> experienced a y-o-y growth in revenue (by 4.5% to $627.4m) despite a moderate y-o-y decline in attendance (by 1.5% to 7.4m) in 2019, followed by a y-o-y decline in revenue (by 74.8% to $158.3m) and a y-o-y decline in attendance (by 79.1% to 1.5m) in 2020.</p>
          <p>In 2019, revenue growth was driven by growth in Theatre (287.0%), Festivals (Contemporary Music) (129.3%) and Classical Music (16.5%). This was partially offset by a decline in Contemporary Music (17.8%), Children&rsquo;s/Family (16.6%) and Opera (10.3%).</p>
          <ul>
            <li>Theatre revenue increased primarily due to an increase in attendance by 51.5%, driven by events such as <em>Harry Potter and the Cursed Child</em>, <em>The Lady in the Van</em>, <em>Shakespeare in Love</em> and <em>Kiss of the Spider Woman</em>. Festivals (Contemporary Music) revenue increased primarily due to an increase in attendance by 61.1%, driven by festivals such as Spilt Milk, Download Festival: Melbourne, Festival X and Groovin&rsquo; the Moo.</li>
            <li>Contemporary Music revenue decline was driven by a decline in attendance and average ticket price. Artists that toured Victoria in 2019 did not attract the same audience numbers as the international artists that toured in 2018 (such as P!NK and Ed Sheeran). Opera revenue decline was primarily driven by a decline in the average ticket price. Children&rsquo;s/Family revenue decline was primarily driven by a decline in attendance, partially offset by a slight growth in the average ticket price.</li>
          </ul>
          <p>As a result of the extended lockdown in Victoria in 2020, all categories experienced a significant decline in revenue and attendance. The revenue decline was primarily driven by a decline in Ballet and Dance (97.4%), Opera (94.7%) and Comedy (89.4%), due to the key companies and events in these categories not being able to present their shows.</p>
          <ul>
            <li>Top events included performances from Queen + Adam Lambert, Elton John and Michael Bubl&eacute;.</li>
          </ul>
          <p><strong>WA</strong> experienced a y-o-y decline in revenue between 2018 and 2020 (by 8.2% to $194.2m in 2019 and 62.5% to $72.7m in 2020) and a y-o-y decline in attendance (by 4.5% to 2.6m in 2019 and 51.4% to 1.3m in 2020).</p>
        </div>
        <div class="sixcol last">
          <p>In 2019, the decline in revenue was driven by a significant decline in Circus and Physical Theatre (60.4%), Children&rsquo;s/Family (41.6%), Musical Theatre (37.4%) and Comedy (30.0%). This was partially offset by a growth in Theatre (125.5%), Festivals (Contemporary Music) (106.1%) and Special Events (25.0%).</p>
          <ul>
            <li>Circus and Physical Theatre and Children&rsquo;s/Family revenue decline was driven by a decline in attendance, despite growth in the average ticket price. Comedy and Musical Theatre revenue decline was driven by a decline in attendance and the average ticket price.&nbsp;</li>
            <li>Theatre revenue increased primarily due to an increase in attendance by 165.9%, driven by events such as <em>Peter Pan Goes Wrong</em>, <em>The Torrents</em> and <em>Black is the New White</em>.</li>
            <li>Festivals (Contemporary Music) revenue increased primarily due to an increase in attendance by 55.7%, driven by festivals such as Falls Festival, Groovin&rsquo; the Moo and St. Jerome's Laneway Festival.</li>
          </ul>
          <p>In 2020, the revenue decline was primarily driven by a decline in Theatre (83.9%), Comedy (80.2%), Contemporary Music (71.5%) and Children&rsquo;s/Family (71.3%), partially offset by the growth in Special Events (131.5%).</p>
          <ul>
            <li>Top events included festivals such as Fringe World Festival, Falls Downtown and performances from Queen + Adam Lambert and Michael Bubl&eacute;.</li>
          </ul>
          <p><strong>SA</strong> experienced a moderate y-o-y growth in revenue (by 1.4% to $133.5m) and a y-o-y growth in attendance (by 4.7% to 2.3m) in 2019, followed by a y-o-y decline in revenue (by 55.3% to $59.7m) and a y-o-y decline in attendance (by 39.9% to 1.4m) in 2020.</p>
          <p>In 2019, the growth in revenue was primarily driven by Circus and Physical Theatre (463.8%), Festivals (Contemporary Music) (236.4%), Classical Music (51.7%) and Musical Theatre (50.7%). The growth in revenue was partially offset by a decline in Children&rsquo;s/Family (34.2%) and Contemporary Music (27.0%).</p>
          <ul>
            <li>Circus and Physical Theatre revenue increased primarily due to an increase in attendance by 887.0%, driven by events performed by Circus Rio and Cosentino. Festivals (Contemporary Music) revenue increased primarily due to an increase in attendance by 115.4%, driven by festivals such as WOMADelaide, Groovin&rsquo; the Moo, Spin Off Festival and St. Jerome's Laneway Festival.</li>
            <li>Classical Music revenue increased primarily due to an increase in attendance by 60.0%, driven by concerts such as <em>Star Wars V</em>, <em>Love Actually In Concert</em> and <em>Harry Potter &amp; the Goblet of Fire</em>. Musical Theatre revenue increased primarily due to an increase in attendance by 45.5%, driven by musicals such as <em>Aladdin</em>, <em>The Book of Mormon</em> and <em>West Side Story</em>.</li>
          </ul>
          <p>In 2020, the revenue decline was primarily driven by a decline in Circus and Physical Theatre (99.8%), Opera (97.9%), Ballet and Dance (91.3%), Musical Theatre (85.4%), Children&rsquo;s/Family (82.3%) and Classical Music (77.4%). The decline in revenue was partially offset by growth in Festivals (Multi-Category) (12.3%) and other events which were presented before the introduction of COVID-19 restrictions.</p>
          <ul>
            <li>Top events included festivals such as Adelaide Fringe and WOMADelaide, performances from Queen + Adam Lambert and Michael Bubl&eacute; and musicals such as <em>Billy Elliot The Musical</em>.</li>
          </ul>
          <p><strong>Australian Capital Territory (ACT)</strong> experienced a y-o-y growth in revenue (by 13.2% to $31.5m) and a y-o-y growth in attendance (by 6.4% to 0.4m) in 2019, followed by a y-o-y decline in revenue (by 81.9% to $5.7m) and a y-o-y decline in attendance (by 76.6% to 0.1m) in 2020.</p>
          <p>The revenue growth in 2019 was driven by significant growth across the Children&rsquo;s/Family, Festivals (Contemporary Music), Musical Theatre and Special Events categories. The decline in revenue in 2020 was primarily impacted by the decline in revenue across Festivals (Contemporary Music), Musical Theatre, Theatre and Ballet and Dance.</p>
          <p><strong>Tasmania</strong> witnessed the highest y-o-y growth (18.8% to $22.5m) in revenue, despite the highest y-o-y decline in attendance (52.5% to 0.4m) in 2019.&nbsp;</p>
          <p>The growth in revenue is primarily driven by a growth in Festivals (Contemporary Music), Circus and Physical Theatre, Contemporary Music and Theatre.</p>
          <p>Tasmania witnessed a y-o-y decline in revenue (by 58.7% to $9.3m) and a y-o-y decline in attendance (by 47.0% to 0.2m) in 2020. All categories except Children&rsquo;s/Family and Musical Theatre, contributed to the decline in revenue.</p>
          <p><strong>Northern Territory (NT)</strong> experienced a y-o-y decline in revenue (by 9.2% to $2.7m) and a y-o-y decline in attendance (by 4.2% to 65k) in 2019, followed by a y-o-y decline in revenue (by 68.0% to $0.8m) and a y-o-y decline in attendance (by 61.5% to 25k) in 2020.</p>
          <p>The revenue decline in 2019 was primarily due to a decline in revenue in Festivals (Multi-Category), Musical Theatre, Circus and Physical Theatre, Children&rsquo;s/Family and Theatre. All categories except Special Events contributed to the decline in revenue in 2020.</p>
        </div>
      </div>
    </article>
    <figure id="state-overall-table" class="data">
      <div class="row clearfix">
        <a href="#" data-table="state-overall-table" class="closetable"
           title="Total revenue and attendances by state/territory (2017-18)">Close Table</a>

        <h3>Total Revenue and Attendances by State/Territory (2017-18)</h3>
        <table cellspacing="0" cellpadding="0">
          <thead>
          <tr>
            <th valign="middle" width="20%">State/Territory</th>
            <th valign="middle" width="10%">Revenue</th>
            <th valign="middle" width="10%">Share of Industry (2018)</th>
            <th valign="middle" width="10%">Share of Industry (2017)</th>
            <th valign="middle" width="10%">Change in Revenue from 2017</th>
            <th valign="middle" width="10%">Tickets</th>
            <th valign="middle" width="10%">Share of Industry (2018)</th>
            <th valign="middle" width="10%">Share of Industry (2016)</th>
            <th valign="middle" width="10%">Change in Attendance from 2017</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td valign="middle">New South Wales</td>
            <td valign="middle" class="number">$753,667,850</td>
            <td valign="middle" class="number">34.9%</td>
            <td valign="middle" class="number">32.7%</td>
            <td valign="middle" class="number">22.4%</td>
            <td valign="middle" class="number">8,230,950</td>
            <td valign="middle" class="number">31.3%</td>
            <td valign="middle" class="number">29.9%</td>
            <td valign="middle" class="number">19.4%</td>
          </tr>
          <tr>
            <td valign="middle">Victoria</td>
            <td valign="middle" class="number">$698,857,788</td>
            <td valign="middle" class="number">32.3%</td>
            <td valign="middle" class="number">34.0%</td>
            <td valign="middle" class="number">9.2%</td>
            <td valign="middle" class="number">7,999,434</td>
            <td valign="middle" class="number">30.5%</td>
            <td valign="middle" class="number">32.0%</td>
            <td valign="middle" class="number">8.6%</td>
          </tr>
          <tr>
            <td valign="middle">Queensland</td>
            <td valign="middle" class="number">$317,179,536</td>
            <td valign="middle" class="number">14.7</td>
            <td valign="middle" class="number">14.0%</td>
            <td valign="middle" class="number">20.2%</td>
            <td valign="middle" class="number">3,840,497</td>
            <td valign="middle" class="number">14.6%</td>
            <td valign="middle" class="number">13.9%</td>
            <td valign="middle" class="number">20.1%</td>
          </tr>
          <tr>
            <td valign="middle">Western Australia</td>
            <td valign="middle" class="number">$211,421,549</td>
            <td valign="middle" class="number">9.8%</td>
            <td valign="middle" class="number">10.4%</td>
            <td valign="middle" class="number">7.5%</td>
            <td valign="middle" class="number">2,772,963</td>
            <td valign="middle" class="number">10.6%</td>
            <td valign="middle" class="number">11.3%</td>
            <td valign="middle" class="number">6.5%</td>
          </tr>
          <tr>
            <td valign="middle">South Australia</td>
            <td valign="middle" class="number">$131,560,865</td>
            <td valign="middle" class="number">6.1%</td>
            <td valign="middle" class="number">6.6%</td>
            <td valign="middle" class="number">6.5%</td>
            <td valign="middle" class="number">2,212,725</td>
            <td valign="middle" class="number">8.4%</td>
            <td valign="middle" class="number">9.1%</td>
            <td valign="middle" class="number">5.4%</td>
          </tr>
          <tr>
            <td valign="middle">Australian Capital Territory</td>
            <td valign="middle" class="number">$27,848,747</td>
            <td valign="middle" class="number">1.3%</td>
            <td valign="middle" class="number">1.3%</td>
            <td valign="middle" class="number">12.3%</td>
            <td valign="middle" class="number">405,899</td>
            <td valign="middle" class="number">1.5%</td>
            <td valign="middle" class="number">1.6%</td>
            <td valign="middle" class="number">12.6%</td>
          </tr>
          <tr>
            <td valign="middle">Tasmania</td>
            <td valign="middle" class="number">$18,965,730</td>
            <td valign="middle" class="number">0.9%</td>
            <td valign="middle" class="number">0.9%</td>
            <td valign="middle" class="number">17.0%</td>
            <td valign="middle" class="number">738,444</td>
            <td valign="middle" class="number">2.8%</td>
            <td valign="middle" class="number">1.9%</td>
            <td valign="middle" class="number">67.1%</td>
          </tr>
          <tr>
            <td valign="middle">Northern Territory</td>
            <td valign="middle" class="number">$2,923,333</td>
            <td valign="middle" class="number">0.1%</td>
            <td valign="middle" class="number">0.2%</td>
            <td valign="middle" class="number">-12.8%</td>
            <td valign="middle" class="number">67,876</td>
            <td valign="middle" class="number">0.3%</td>
            <td valign="middle" class="number">0.3%</td>
            <td valign="middle" class="number">1.9%</td>
          </tr>
          <tr>
            <td valign="middle">Total</td>
            <td valign="middle" class="number">$1,884,141,882</td>
            <td valign="middle" class="number">100.0%</td>
            <td valign="middle" class="number">100.0%</td>
            <td valign="middle" class="number">31.7%</td>
            <td valign="middle" class="number">23,027,550</td>
            <td valign="middle" class="number">100.0%</td>
            <td valign="middle" class="number">100.0%</td>
            <td valign="middle" class="number">22.6%</td>
          </tr>
          </tbody>
        </table>
      </div>
    </figure>
  </section>
</template>

<script>
/*eslint-disable */

export default {
  data () {
    return {
      activeYear: 2019,
      years: [2019, 2020],
      reports: [
        {
          year: 2019,
          revenue: [
            {
              'hc-key': 'au-ns',
              label: 'NSW',
              z: 33.9,
              value: '33.9',
              labelsize: 1.7,
              absolute: '$666.74m'
            }, {
              'hc-key': 'au-vi',
              label: 'VIC',
              z: 31.9,
              value: '31.9',
              labelsize: 1.25,
              absolute: '$627.38m'
            }, {
              'hc-key': 'au-ql',
              label: 'QLD',
              z: 14.6,
              value: '14.6',
              labelsize: 1.25,
              absolute: '$286.19m'
            }, {
              'hc-key': 'au-wa',
              label: 'WA',
              z: 9.9,
              value: '9.9',
              labelsize: 1.25,
              absolute: '$194.18m'
            }, {
              'hc-key': 'au-sa',
              label: 'SA',
              z: 6.8,
              value: '6.8',
              labelsize: 1.25,
              absolute: '$133.46m'
            }, {
              'hc-key': 'au-ct',
              label: 'ACT',
              z: 1.6,
              value: '1.6',
              labelsize: 1.25,
              absolute: '$31.52m'
            }, {
              'hc-key': 'au-ts',
              label: 'TAS',
              z: 1.1,
              value: '1.1',
              labelsize: 1.25,
              absolute: '$22.52m'
            }, {
              'hc-key': 'au-nt',
              label: 'NT',
              z: 0.1,
              value: '0.1',
              labelsize: 1.25,
              absolute: '$2.65m'
            }
          ],
          attendance: [
            {
              'hc-key': 'au-ns',
              label: 'NSW',
              z: 30.0,
              value: '30.0',
              labelsize: 1.25,
              absolute: '7.16m'
            }, {
              'hc-key': 'au-vi',
              label: 'VIC',
              z: 31.0,
              value: '31.0',
              labelsize: 1.25,
              absolute: '7.41m'
            }, {
              'hc-key': 'au-ql',
              label: 'QLD',
              z: 14.6,
              value: '14.6',
              labelsize: 1.25,
              absolute: '3.48m'
            }, {
              'hc-key': 'au-wa',
              label: 'WA',
              z: 11.1,
              value: '11.1',
              labelsize: 1.25,
              absolute: '2.65m'
            }, {
              'hc-key': 'au-sa',
              label: 'SA',
              z: 9.7,
              value: '9.7',
              labelsize: 1.25,
              absolute: '2.32m'
            }, {
              'hc-key': 'au-ct',
              label: 'ACT',
              z: 1.8,
              value: '1.8',
              labelsize: 1.25,
              absolute: '431,706'
            }, {
              'hc-key': 'au-ts',
              label: 'TAS',
              z: 1.5,
              value: '1.5',
              labelsize: 1.25,
              absolute: '350,977'
            }, {
              'hc-key': 'au-nt',
              label: 'NT',
              z: 0.3,
              value: '0.3',
              labelsize: 1.25,
              absolute: '65,045'
            }
          ]
        },
        {
          year: 2020,
          revenue: [
            {
              'hc-key': 'au-ns',
              label: 'NSW',
              z: 30.1,
              value: '30.1',
              labelsize: 1.7,
              absolute: '$182.52m'
            }, {
              'hc-key': 'au-vi',
              label: 'VIC',
              z: 26.1,
              value: '26.1',
              labelsize: 1.25,
              absolute: '$158.29m'
            }, {
              'hc-key': 'au-ql',
              label: 'QLD',
              z: 19.3,
              value: '19.3',
              labelsize: 1.25,
              absolute: '$116.87m'
            }, {
              'hc-key': 'au-wa',
              label: 'WA',
              z: 12.0,
              value: '12.0',
              labelsize: 1.25,
              absolute: '$72.74m'
            }, {
              'hc-key': 'au-sa',
              label: 'SA',
              z: 9.9,
              value: '9.9',
              labelsize: 1.25,
              absolute: '$59.70m'
            }, {
              'hc-key': 'au-ct',
              label: 'ACT',
              z: 0.9,
              value: '0.9',
              labelsize: 1.25,
              absolute: '$5.70m'
            }, {
              'hc-key': 'au-ts',
              label: 'TAS',
              z: 1.5,
              value: '1.5',
              labelsize: 1.25,
              absolute: '$9.30m'
            }, {
              'hc-key': 'au-nt',
              label: 'NT',
              z: 0.1,
              value: '0.1',
              labelsize: 1.25,
              absolute: '$0.85m'
            }
          ],
          attendance: [
            {
              'hc-key': 'au-ns',
              label: 'NSW',
              z: 25.3,
              value: '25.3',
              labelsize: 1.25,
              absolute: '1.96m'
            }, {
              'hc-key': 'au-vi',
              label: 'VIC',
              z: 19.9,
              value: '19.9',
              labelsize: 1.25,
              absolute: '1.55m'
            }, {
              'hc-key': 'au-ql',
              label: 'QLD',
              z: 16.2,
              value: '16.2',
              labelsize: 1.25,
              absolute: '1.26m'
            }, {
              'hc-key': 'au-wa',
              label: 'WA',
              z: 16.6,
              value: '16.6',
              labelsize: 1.25,
              absolute: '1.29m'
            }, {
              'hc-key': 'au-sa',
              label: 'SA',
              z: 18.0,
              value: '18.0',
              labelsize: 1.25,
              absolute: '1.39m'
            }, {
              'hc-key': 'au-ct',
              label: 'ACT',
              z: 1.3,
              value: '1.3',
              labelsize: 1.25,
              absolute: '101,081'
            }, {
              'hc-key': 'au-ts',
              label: 'TAS',
              z: 2.4,
              value: '2.4',
              labelsize: 1.25,
              absolute: '186,150'
            }, {
              'hc-key': 'au-nt',
              label: 'NT',
              z: 0.3,
              value: '0.3',
              labelsize: 1.25,
              absolute: '25,035'
            }
          ]
        },
      ],
    }
  },
  computed: {
    revAttByState () {
      const self = this

      for (let i = 0; i < self.reports.length; i++) {
        const report = self.reports[i]
        if (report.year === self.activeYear) {
          return {
            chart: {
              backgroundColor: 'transparent'
            },
            title: {
              text: ''
            },
            mapNavigation: {
              enabled: false
            },
            legend: {
              enabled: false
            },
            plotOptions: {
              series: {
                states: {
                  inactive: {
                    opacity: 1
                  }
                }
              }
            },
            tooltip: {
              formatter () {
                return this.key + '<br />' + this.series.name + ': ' + this.point.value + '% (' + this.point.absolute + ')'
              }
            },
            series: [{
              color: '#e2c655',
              borderWidth: '0',
              enableMouseTracking: false,
              data: [{
                'hc-key': 'au-ns'
              }, {
                'hc-key': 'au-vi'
              }, {
                'hc-key': 'au-ql'
              }, {
                'hc-key': 'au-wa'
              }, {
                'hc-key': 'au-sa'
              }, {
                'hc-key': 'au-ct'
              }, {
                'hc-key': 'au-ts'
              }, {
                'hc-key': 'au-nt'
              }],
              mapData: {
                "title": "Australia",
                "version": "1.0.2",
                "type": "FeatureCollection",
                "copyright": "Copyright (c) 2014 Highsoft AS, Based on data from Natural Earth http://www.naturalearthdata.com",
                "copyrightShort": "Natural Earth",
                "copyrightUrl": "http://www.naturalearthdata.com",
                "crs": {
                  "type": "name",
                  "properties": {
                    "name": "urn:ogc:def:crs:EPSG:3112"
                  }
                },
                "features": [{
                  "type": "Feature",
                  "id": "AU.NT",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.50,
                    "hc-middle-y": 0.57,
                    "hc-key": "au-nt",
                    "hc-a2": "NT",
                    "labelrank": "2",
                    "hasc": "AU.NT",
                    "alt-name": null,
                    "woe-id": "2344701",
                    "subregion": null,
                    "fips": "AS03",
                    "postal-code": "NT",
                    "name": "Northern Territory",
                    "country": "Australia",
                    "type-en": "Territory",
                    "region": null,
                    "longitude": "133.78",
                    "woe-name": "Northern Territory",
                    "latitude": "-20.1026",
                    "woe-label": "Northern Territory, AU, Australia",
                    "type": "Territory"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[932, 1724], [936, 1727], [944, 1721], [940, 1711], [951, 1704], [948, 1701], [928, 1706], [928, 1723], [932, 1724]]], [[[663, 1826], [666, 1816], [674, 1812], [649, 1810], [655, 1818], [655, 1825], [661, 1833], [663, 1826]]], [[[668, 1835], [678, 1830], [691, 1832], [700, 1839], [709, 1837], [714, 1827], [704, 1816], [690, 1806], [668, 1818], [662, 1837], [668, 1835]]], [[[992, 1601], [992, 1601], [992, 1600], [979, 1190], [627, 1188], [608, 1673], [608, 1673], [620, 1663], [618, 1675], [629, 1671], [637, 1662], [630, 1677], [643, 1675], [633, 1687], [623, 1694], [630, 1709], [638, 1712], [644, 1738], [655, 1737], [660, 1745], [654, 1751], [654, 1760], [662, 1762], [664, 1774], [672, 1775], [675, 1783], [694, 1787], [692, 1798], [713, 1790], [731, 1794], [737, 1790], [765, 1798], [762, 1820], [751, 1830], [739, 1825], [731, 1834], [724, 1835], [734, 1843], [744, 1831], [743, 1843], [750, 1844], [759, 1835], [755, 1848], [759, 1850], [764, 1826], [773, 1835], [787, 1817], [805, 1814], [814, 1818], [820, 1809], [826, 1811], [842, 1802], [857, 1807], [870, 1793], [883, 1801], [893, 1800], [893, 1806], [910, 1811], [895, 1797], [899, 1790], [906, 1798], [913, 1794], [912, 1784], [922, 1784], [927, 1792], [916, 1801], [931, 1806], [941, 1791], [946, 1797], [951, 1788], [932, 1766], [938, 1761], [931, 1749], [926, 1756], [917, 1748], [905, 1745], [905, 1727], [912, 1731], [920, 1722], [910, 1723], [905, 1707], [899, 1704], [882, 1684], [886, 1674], [919, 1652], [929, 1640], [934, 1648], [938, 1630], [950, 1632], [966, 1620], [982, 1614], [992, 1601]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.WA",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.57,
                    "hc-middle-y": 0.54,
                    "hc-key": "au-wa",
                    "hc-a2": "WA",
                    "labelrank": "2",
                    "hasc": "AU.WA",
                    "alt-name": null,
                    "woe-id": "2344706",
                    "subregion": null,
                    "fips": "AS08",
                    "postal-code": "WA",
                    "name": "Western Australia",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "121.646",
                    "woe-name": "Western Australia",
                    "latitude": "-25.8483",
                    "woe-label": "Western Australia, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[0, 1146], [14, 1124], [7, 1127], [0, 1139], [0, 1146]]], [[[608, 1673], [608, 1673], [627, 1188], [637, 958], [637, 942], [602, 924], [575, 914], [553, 911], [534, 913], [523, 908], [511, 898], [492, 889], [482, 881], [469, 877], [461, 869], [457, 850], [443, 832], [429, 828], [426, 834], [399, 829], [390, 825], [387, 832], [380, 829], [370, 831], [335, 824], [330, 820], [315, 820], [301, 809], [291, 791], [276, 793], [274, 786], [264, 780], [262, 771], [238, 762], [236, 757], [226, 761], [200, 756], [176, 762], [160, 777], [149, 782], [142, 778], [135, 790], [132, 814], [143, 810], [148, 813], [155, 828], [148, 854], [152, 860], [149, 887], [145, 898], [121, 931], [109, 956], [105, 987], [97, 1003], [85, 1015], [82, 1026], [65, 1041], [61, 1059], [54, 1074], [33, 1099], [20, 1109], [22, 1119], [32, 1103], [41, 1104], [42, 1115], [34, 1119], [20, 1138], [24, 1144], [34, 1133], [38, 1120], [41, 1131], [45, 1116], [51, 1112], [53, 1139], [49, 1140], [27, 1166], [20, 1182], [12, 1189], [8, 1210], [12, 1229], [17, 1235], [17, 1255], [7, 1274], [16, 1307], [22, 1311], [21, 1294], [28, 1280], [35, 1283], [42, 1312], [72, 1331], [99, 1365], [107, 1367], [121, 1381], [125, 1374], [135, 1380], [144, 1376], [159, 1381], [174, 1396], [201, 1404], [206, 1413], [215, 1418], [230, 1415], [240, 1421], [257, 1426], [285, 1441], [294, 1449], [306, 1467], [316, 1495], [320, 1495], [335, 1511], [336, 1517], [328, 1517], [325, 1549], [340, 1571], [351, 1572], [348, 1580], [353, 1589], [377, 1550], [384, 1543], [387, 1558], [384, 1566], [396, 1557], [387, 1578], [375, 1587], [387, 1591], [379, 1606], [389, 1604], [387, 1598], [399, 1594], [395, 1601], [414, 1594], [432, 1596], [416, 1598], [423, 1615], [415, 1617], [413, 1633], [423, 1634], [437, 1659], [452, 1652], [456, 1658], [442, 1669], [441, 1681], [446, 1676], [461, 1680], [461, 1694], [465, 1682], [470, 1685], [475, 1676], [480, 1682], [483, 1701], [487, 1696], [496, 1707], [504, 1696], [509, 1716], [518, 1720], [526, 1709], [535, 1712], [549, 1701], [559, 1689], [574, 1679], [569, 1660], [582, 1675], [588, 1677], [608, 1673]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.SA",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.54,
                    "hc-middle-y": 0.36,
                    "hc-key": "au-sa",
                    "hc-a2": "SA",
                    "labelrank": "2",
                    "hasc": "AU.SA",
                    "alt-name": null,
                    "woe-id": "2344703",
                    "subregion": null,
                    "fips": "AS05",
                    "postal-code": "SA",
                    "name": "South Australia",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "135.783",
                    "woe-name": "South Australia",
                    "latitude": "-29.6504",
                    "woe-label": "South Australia, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[953, 774], [951, 768], [968, 766], [967, 759], [957, 762], [946, 753], [937, 757], [920, 754], [913, 761], [915, 767], [943, 775], [953, 774]]], [[[637, 942], [637, 958], [627, 1188], [979, 1190], [1096, 1185], [1089, 1055], [1077, 838], [1076, 839], [1066, 661], [1057, 661], [1045, 670], [1042, 679], [1033, 688], [1025, 701], [1023, 712], [1030, 719], [1030, 726], [1021, 750], [1003, 772], [1007, 776], [1010, 767], [1015, 777], [1012, 783], [1001, 780], [1002, 774], [993, 775], [985, 771], [973, 770], [970, 775], [982, 784], [986, 810], [977, 824], [971, 837], [955, 792], [945, 795], [937, 790], [925, 790], [932, 805], [947, 804], [950, 817], [948, 837], [954, 849], [967, 864], [962, 876], [970, 880], [964, 906], [961, 887], [949, 881], [940, 859], [918, 849], [909, 841], [904, 831], [893, 822], [891, 811], [895, 802], [881, 807], [865, 823], [874, 817], [872, 833], [866, 850], [854, 861], [853, 874], [847, 879], [833, 882], [826, 901], [834, 903], [828, 913], [817, 909], [821, 916], [810, 928], [801, 923], [791, 924], [778, 934], [758, 931], [731, 949], [718, 954], [705, 948], [680, 949], [637, 942]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.ACT",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.37,
                    "hc-middle-y": 0.48,
                    "hc-key": "au-ct",
                    "hc-a2": "CT",
                    "labelrank": "9",
                    "hasc": "AU.CT",
                    "alt-name": null,
                    "woe-id": "1100968",
                    "subregion": null,
                    "fips": "AS01",
                    "postal-code": "CT",
                    "name": "Australian Capital Territory",
                    "country": "Australia",
                    "type-en": "Territory",
                    "region": null,
                    "longitude": "148.983",
                    "woe-name": "Canberra",
                    "latitude": "-35.4618",
                    "woe-label": null,
                    "type": "Territory"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1373, 753], [1366, 752], [1361, 743], [1361, 733], [1355, 731], [1350, 740], [1354, 757], [1364, 763], [1373, 753]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.QLD",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.44,
                    "hc-middle-y": 0.66,
                    "hc-key": "au-ql",
                    "hc-a2": "QL",
                    "labelrank": "2",
                    "hasc": "AU.QL",
                    "alt-name": null,
                    "woe-id": "2344702",
                    "subregion": null,
                    "fips": "AS04",
                    "postal-code": "QL",
                    "name": "Queensland",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "144.778",
                    "woe-name": "Queensland",
                    "latitude": "-23.1364",
                    "woe-label": "Queensland, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[1582, 1189], [1584, 1176], [1566, 1145], [1564, 1156], [1570, 1171], [1579, 1180], [1576, 1186], [1582, 1189]]], [[[1502, 1257], [1508, 1252], [1507, 1242], [1499, 1255], [1502, 1257]]], [[[1552, 1035], [1538, 1037], [1529, 1043], [1507, 1034], [1507, 1019], [1489, 1018], [1480, 1010], [1479, 1021], [1462, 1036], [1449, 1037], [1444, 1042], [1433, 1041], [1413, 1044], [1391, 1029], [1089, 1055], [1096, 1185], [979, 1190], [992, 1600], [992, 1601], [1000, 1594], [1020, 1590], [1036, 1583], [1045, 1564], [1075, 1547], [1096, 1549], [1110, 1556], [1122, 1584], [1127, 1588], [1139, 1615], [1137, 1621], [1143, 1641], [1152, 1661], [1147, 1685], [1151, 1703], [1147, 1708], [1149, 1725], [1158, 1739], [1154, 1751], [1165, 1765], [1155, 1771], [1165, 1792], [1171, 1789], [1182, 1823], [1185, 1841], [1198, 1850], [1205, 1848], [1214, 1818], [1213, 1798], [1227, 1791], [1221, 1776], [1235, 1762], [1231, 1751], [1237, 1748], [1239, 1721], [1236, 1711], [1241, 1699], [1244, 1682], [1251, 1676], [1261, 1682], [1264, 1692], [1267, 1684], [1277, 1689], [1282, 1672], [1291, 1669], [1296, 1659], [1309, 1652], [1304, 1646], [1304, 1626], [1310, 1603], [1306, 1586], [1320, 1565], [1327, 1565], [1323, 1558], [1332, 1531], [1324, 1504], [1336, 1504], [1337, 1497], [1332, 1477], [1338, 1468], [1365, 1450], [1378, 1451], [1386, 1429], [1389, 1435], [1396, 1425], [1410, 1421], [1414, 1411], [1421, 1416], [1422, 1408], [1434, 1404], [1422, 1393], [1429, 1380], [1443, 1368], [1449, 1339], [1447, 1336], [1452, 1316], [1467, 1296], [1464, 1312], [1470, 1318], [1475, 1306], [1490, 1295], [1488, 1312], [1496, 1300], [1498, 1290], [1493, 1271], [1493, 1260], [1515, 1230], [1528, 1228], [1539, 1200], [1548, 1193], [1555, 1171], [1564, 1167], [1562, 1147], [1571, 1138], [1564, 1124], [1564, 1090], [1557, 1085], [1566, 1060], [1571, 1074], [1571, 1076], [1571, 1076], [1571, 1076], [1575, 1073], [1567, 1051], [1568, 1041], [1552, 1035]]], [[[1043, 1594], [1047, 1602], [1060, 1605], [1053, 1593], [1043, 1594], [1043, 1594], [1043, 1594]]], [[[1571, 1077], [1571, 1076], [1571, 1076], [1571, 1076], [1570, 1076], [1571, 1077], [1571, 1077]]], [[[1571, 1077], [1570, 1089], [1573, 1090], [1571, 1077], [1571, 1077]]], [[[1043, 1594], [1041, 1587], [1042, 1594], [1043, 1594], [1043, 1594], [1043, 1594]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.TAS",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.61,
                    "hc-middle-y": 0.65,
                    "hc-key": "au-ts",
                    "hc-a2": "TS",
                    "labelrank": "2",
                    "hasc": "AU.TS",
                    "alt-name": null,
                    "woe-id": "2344704",
                    "subregion": null,
                    "fips": "AS06",
                    "postal-code": "TS",
                    "name": "Tasmania",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "146.603",
                    "woe-name": null,
                    "latitude": "-42.1383",
                    "woe-label": null,
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[1307, 532], [1300, 538], [1311, 540], [1315, 534], [1307, 532]]], [[[1301, 568], [1311, 555], [1310, 543], [1304, 542], [1293, 561], [1301, 568]]], [[[1167, 565], [1162, 562], [1161, 581], [1169, 584], [1169, 568], [1167, 565]]], [[[1188, 539], [1187, 536], [1200, 528], [1219, 521], [1222, 517], [1242, 509], [1263, 515], [1277, 512], [1283, 519], [1291, 516], [1297, 522], [1307, 509], [1301, 474], [1303, 459], [1296, 463], [1290, 452], [1286, 437], [1284, 411], [1278, 411], [1274, 421], [1281, 419], [1281, 426], [1271, 429], [1272, 424], [1261, 419], [1263, 406], [1255, 411], [1245, 396], [1236, 402], [1225, 405], [1219, 403], [1217, 419], [1213, 415], [1210, 427], [1204, 431], [1198, 448], [1197, 466], [1204, 458], [1198, 477], [1190, 489], [1182, 522], [1188, 539]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.NSW",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.44,
                    "hc-middle-y": 0.39,
                    "hc-key": "au-ns",
                    "hc-a2": "NS",
                    "labelrank": "2",
                    "hasc": "AU.NS",
                    "alt-name": null,
                    "woe-id": "2344700",
                    "subregion": null,
                    "fips": "AS02",
                    "postal-code": "NS",
                    "name": "New South Wales",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "146.781",
                    "woe-name": "New South Wales",
                    "latitude": "-32.4751",
                    "woe-label": "New South Wales, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1418, 754], [1398, 726], [1395, 707], [1383, 682], [1386, 666], [1382, 656], [1324, 694], [1325, 701], [1320, 728], [1311, 734], [1300, 734], [1297, 729], [1279, 730], [1272, 736], [1263, 734], [1241, 740], [1235, 746], [1215, 747], [1214, 737], [1205, 736], [1194, 746], [1191, 754], [1160, 781], [1160, 798], [1138, 808], [1131, 801], [1126, 812], [1127, 820], [1121, 827], [1104, 832], [1097, 828], [1077, 838], [1089, 1055], [1391, 1029], [1413, 1044], [1433, 1041], [1444, 1042], [1449, 1037], [1462, 1036], [1479, 1021], [1480, 1010], [1489, 1018], [1507, 1019], [1507, 1034], [1529, 1043], [1538, 1037], [1552, 1035], [1568, 1041], [1570, 1036], [1567, 1010], [1554, 992], [1551, 976], [1531, 934], [1534, 926], [1525, 902], [1505, 875], [1504, 863], [1486, 850], [1476, 848], [1453, 819], [1449, 804], [1434, 790], [1428, 771], [1427, 761], [1421, 757], [1419, 756], [1418, 754]], [[1373, 753], [1364, 763], [1354, 757], [1350, 740], [1355, 731], [1361, 733], [1361, 743], [1366, 752], [1373, 753]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.VIC",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.38,
                    "hc-middle-y": 0.62,
                    "hc-key": "au-vi",
                    "hc-a2": "VI",
                    "labelrank": "2",
                    "hasc": "AU.VI",
                    "alt-name": null,
                    "woe-id": "2344705",
                    "subregion": null,
                    "fips": "AS07",
                    "postal-code": "VI",
                    "name": "Victoria",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "144.75",
                    "woe-name": "Victoria",
                    "latitude": "-37.0082",
                    "woe-label": "Victoria, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1066, 661], [1076, 839], [1077, 838], [1097, 828], [1104, 832], [1121, 827], [1127, 820], [1126, 812], [1131, 801], [1138, 808], [1160, 798], [1160, 781], [1191, 754], [1194, 746], [1205, 736], [1214, 737], [1215, 747], [1235, 746], [1241, 740], [1263, 734], [1272, 736], [1279, 730], [1297, 729], [1300, 734], [1311, 734], [1320, 728], [1325, 701], [1324, 694], [1382, 656], [1375, 655], [1363, 646], [1322, 649], [1308, 647], [1296, 641], [1269, 619], [1245, 616], [1248, 609], [1255, 614], [1251, 598], [1241, 614], [1235, 610], [1232, 622], [1209, 629], [1215, 635], [1216, 642], [1203, 631], [1198, 637], [1205, 638], [1210, 646], [1206, 658], [1201, 658], [1190, 650], [1197, 647], [1194, 642], [1185, 642], [1172, 635], [1164, 626], [1153, 619], [1137, 631], [1129, 634], [1115, 644], [1109, 643], [1093, 650], [1089, 644], [1079, 645], [1080, 650], [1066, 661]]]
                  }
                }
                ]
              },
              joinBy: 'hc-key'
            }, {
              type: 'mapbubble',
              color: 'rgba(50,158,48,0.2)',
              enableMouseTracking: true,
              data: report.revenue,
              name: 'Revenue',
              mapData: {
                "title": "Australia",
                "version": "1.0.2",
                "type": "FeatureCollection",
                "copyright": "Copyright (c) 2014 Highsoft AS, Based on data from Natural Earth http://www.naturalearthdata.com",
                "copyrightShort": "Natural Earth",
                "copyrightUrl": "http://www.naturalearthdata.com",
                "crs": {
                  "type": "name",
                  "properties": {
                    "name": "urn:ogc:def:crs:EPSG:3112"
                  }
                },
                "features": [{
                  "type": "Feature",
                  "id": "AU.NT",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.65,
                    "hc-middle-y": 0.57,
                    "hc-key": "au-nt",
                    "hc-a2": "NT",
                    "labelrank": "2",
                    "hasc": "AU.NT",
                    "alt-name": null,
                    "woe-id": "2344701",
                    "subregion": null,
                    "fips": "AS03",
                    "postal-code": "NT",
                    "name": "Northern Territory",
                    "country": "Australia",
                    "type-en": "Territory",
                    "region": null,
                    "longitude": "133.78",
                    "woe-name": "Northern Territory",
                    "latitude": "-20.1026",
                    "woe-label": "Northern Territory, AU, Australia",
                    "type": "Territory"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[932, 1724], [936, 1727], [944, 1721], [940, 1711], [951, 1704], [948, 1701], [928, 1706], [928, 1723], [932, 1724]]], [[[663, 1826], [666, 1816], [674, 1812], [649, 1810], [655, 1818], [655, 1825], [661, 1833], [663, 1826]]], [[[668, 1835], [678, 1830], [691, 1832], [700, 1839], [709, 1837], [714, 1827], [704, 1816], [690, 1806], [668, 1818], [662, 1837], [668, 1835]]], [[[992, 1601], [992, 1601], [992, 1600], [979, 1190], [627, 1188], [608, 1673], [608, 1673], [620, 1663], [618, 1675], [629, 1671], [637, 1662], [630, 1677], [643, 1675], [633, 1687], [623, 1694], [630, 1709], [638, 1712], [644, 1738], [655, 1737], [660, 1745], [654, 1751], [654, 1760], [662, 1762], [664, 1774], [672, 1775], [675, 1783], [694, 1787], [692, 1798], [713, 1790], [731, 1794], [737, 1790], [765, 1798], [762, 1820], [751, 1830], [739, 1825], [731, 1834], [724, 1835], [734, 1843], [744, 1831], [743, 1843], [750, 1844], [759, 1835], [755, 1848], [759, 1850], [764, 1826], [773, 1835], [787, 1817], [805, 1814], [814, 1818], [820, 1809], [826, 1811], [842, 1802], [857, 1807], [870, 1793], [883, 1801], [893, 1800], [893, 1806], [910, 1811], [895, 1797], [899, 1790], [906, 1798], [913, 1794], [912, 1784], [922, 1784], [927, 1792], [916, 1801], [931, 1806], [941, 1791], [946, 1797], [951, 1788], [932, 1766], [938, 1761], [931, 1749], [926, 1756], [917, 1748], [905, 1745], [905, 1727], [912, 1731], [920, 1722], [910, 1723], [905, 1707], [899, 1704], [882, 1684], [886, 1674], [919, 1652], [929, 1640], [934, 1648], [938, 1630], [950, 1632], [966, 1620], [982, 1614], [992, 1601]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.WA",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.67,
                    "hc-middle-y": 0.54,
                    "hc-key": "au-wa",
                    "hc-a2": "WA",
                    "labelrank": "2",
                    "hasc": "AU.WA",
                    "alt-name": null,
                    "woe-id": "2344706",
                    "subregion": null,
                    "fips": "AS08",
                    "postal-code": "WA",
                    "name": "Western Australia",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "121.646",
                    "woe-name": "Western Australia",
                    "latitude": "-25.8483",
                    "woe-label": "Western Australia, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[0, 1146], [14, 1124], [7, 1127], [0, 1139], [0, 1146]]], [[[608, 1673], [608, 1673], [627, 1188], [637, 958], [637, 942], [602, 924], [575, 914], [553, 911], [534, 913], [523, 908], [511, 898], [492, 889], [482, 881], [469, 877], [461, 869], [457, 850], [443, 832], [429, 828], [426, 834], [399, 829], [390, 825], [387, 832], [380, 829], [370, 831], [335, 824], [330, 820], [315, 820], [301, 809], [291, 791], [276, 793], [274, 786], [264, 780], [262, 771], [238, 762], [236, 757], [226, 761], [200, 756], [176, 762], [160, 777], [149, 782], [142, 778], [135, 790], [132, 814], [143, 810], [148, 813], [155, 828], [148, 854], [152, 860], [149, 887], [145, 898], [121, 931], [109, 956], [105, 987], [97, 1003], [85, 1015], [82, 1026], [65, 1041], [61, 1059], [54, 1074], [33, 1099], [20, 1109], [22, 1119], [32, 1103], [41, 1104], [42, 1115], [34, 1119], [20, 1138], [24, 1144], [34, 1133], [38, 1120], [41, 1131], [45, 1116], [51, 1112], [53, 1139], [49, 1140], [27, 1166], [20, 1182], [12, 1189], [8, 1210], [12, 1229], [17, 1235], [17, 1255], [7, 1274], [16, 1307], [22, 1311], [21, 1294], [28, 1280], [35, 1283], [42, 1312], [72, 1331], [99, 1365], [107, 1367], [121, 1381], [125, 1374], [135, 1380], [144, 1376], [159, 1381], [174, 1396], [201, 1404], [206, 1413], [215, 1418], [230, 1415], [240, 1421], [257, 1426], [285, 1441], [294, 1449], [306, 1467], [316, 1495], [320, 1495], [335, 1511], [336, 1517], [328, 1517], [325, 1549], [340, 1571], [351, 1572], [348, 1580], [353, 1589], [377, 1550], [384, 1543], [387, 1558], [384, 1566], [396, 1557], [387, 1578], [375, 1587], [387, 1591], [379, 1606], [389, 1604], [387, 1598], [399, 1594], [395, 1601], [414, 1594], [432, 1596], [416, 1598], [423, 1615], [415, 1617], [413, 1633], [423, 1634], [437, 1659], [452, 1652], [456, 1658], [442, 1669], [441, 1681], [446, 1676], [461, 1680], [461, 1694], [465, 1682], [470, 1685], [475, 1676], [480, 1682], [483, 1701], [487, 1696], [496, 1707], [504, 1696], [509, 1716], [518, 1720], [526, 1709], [535, 1712], [549, 1701], [559, 1689], [574, 1679], [569, 1660], [582, 1675], [588, 1677], [608, 1673]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.SA",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.6,
                    "hc-middle-y": 0.26,
                    "hc-key": "au-sa",
                    "hc-a2": "SA",
                    "labelrank": "2",
                    "hasc": "AU.SA",
                    "alt-name": null,
                    "woe-id": "2344703",
                    "subregion": null,
                    "fips": "AS05",
                    "postal-code": "SA",
                    "name": "South Australia",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "135.783",
                    "woe-name": "South Australia",
                    "latitude": "-29.6504",
                    "woe-label": "South Australia, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[953, 774], [951, 768], [968, 766], [967, 759], [957, 762], [946, 753], [937, 757], [920, 754], [913, 761], [915, 767], [943, 775], [953, 774]]], [[[637, 942], [637, 958], [627, 1188], [979, 1190], [1096, 1185], [1089, 1055], [1077, 838], [1076, 839], [1066, 661], [1057, 661], [1045, 670], [1042, 679], [1033, 688], [1025, 701], [1023, 712], [1030, 719], [1030, 726], [1021, 750], [1003, 772], [1007, 776], [1010, 767], [1015, 777], [1012, 783], [1001, 780], [1002, 774], [993, 775], [985, 771], [973, 770], [970, 775], [982, 784], [986, 810], [977, 824], [971, 837], [955, 792], [945, 795], [937, 790], [925, 790], [932, 805], [947, 804], [950, 817], [948, 837], [954, 849], [967, 864], [962, 876], [970, 880], [964, 906], [961, 887], [949, 881], [940, 859], [918, 849], [909, 841], [904, 831], [893, 822], [891, 811], [895, 802], [881, 807], [865, 823], [874, 817], [872, 833], [866, 850], [854, 861], [853, 874], [847, 879], [833, 882], [826, 901], [834, 903], [828, 913], [817, 909], [821, 916], [810, 928], [801, 923], [791, 924], [778, 934], [758, 931], [731, 949], [718, 954], [705, 948], [680, 949], [637, 942]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.ACT",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 3.77,
                    "hc-middle-y": 0.18,
                    "hc-key": "au-ct",
                    "hc-a2": "CT",
                    "labelrank": "9",
                    "hasc": "AU.CT",
                    "alt-name": null,
                    "woe-id": "1100968",
                    "subregion": null,
                    "fips": "AS01",
                    "postal-code": "CT",
                    "name": "Australian Capital Territory",
                    "country": "Australia",
                    "type-en": "Territory",
                    "region": null,
                    "longitude": "148.983",
                    "woe-name": "Canberra",
                    "latitude": "-35.4618",
                    "woe-label": null,
                    "type": "Territory"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1373, 753], [1366, 752], [1361, 743], [1361, 733], [1355, 731], [1350, 740], [1354, 757], [1364, 763], [1373, 753]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.QLD",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.5,
                    "hc-middle-y": 0.6,
                    "hc-key": "au-ql",
                    "hc-a2": "QL",
                    "labelrank": "2",
                    "hasc": "AU.QL",
                    "alt-name": null,
                    "woe-id": "2344702",
                    "subregion": null,
                    "fips": "AS04",
                    "postal-code": "QL",
                    "name": "Queensland",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "144.778",
                    "woe-name": "Queensland",
                    "latitude": "-23.1364",
                    "woe-label": "Queensland, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[1582, 1189], [1584, 1176], [1566, 1145], [1564, 1156], [1570, 1171], [1579, 1180], [1576, 1186], [1582, 1189]]], [[[1502, 1257], [1508, 1252], [1507, 1242], [1499, 1255], [1502, 1257]]], [[[1552, 1035], [1538, 1037], [1529, 1043], [1507, 1034], [1507, 1019], [1489, 1018], [1480, 1010], [1479, 1021], [1462, 1036], [1449, 1037], [1444, 1042], [1433, 1041], [1413, 1044], [1391, 1029], [1089, 1055], [1096, 1185], [979, 1190], [992, 1600], [992, 1601], [1000, 1594], [1020, 1590], [1036, 1583], [1045, 1564], [1075, 1547], [1096, 1549], [1110, 1556], [1122, 1584], [1127, 1588], [1139, 1615], [1137, 1621], [1143, 1641], [1152, 1661], [1147, 1685], [1151, 1703], [1147, 1708], [1149, 1725], [1158, 1739], [1154, 1751], [1165, 1765], [1155, 1771], [1165, 1792], [1171, 1789], [1182, 1823], [1185, 1841], [1198, 1850], [1205, 1848], [1214, 1818], [1213, 1798], [1227, 1791], [1221, 1776], [1235, 1762], [1231, 1751], [1237, 1748], [1239, 1721], [1236, 1711], [1241, 1699], [1244, 1682], [1251, 1676], [1261, 1682], [1264, 1692], [1267, 1684], [1277, 1689], [1282, 1672], [1291, 1669], [1296, 1659], [1309, 1652], [1304, 1646], [1304, 1626], [1310, 1603], [1306, 1586], [1320, 1565], [1327, 1565], [1323, 1558], [1332, 1531], [1324, 1504], [1336, 1504], [1337, 1497], [1332, 1477], [1338, 1468], [1365, 1450], [1378, 1451], [1386, 1429], [1389, 1435], [1396, 1425], [1410, 1421], [1414, 1411], [1421, 1416], [1422, 1408], [1434, 1404], [1422, 1393], [1429, 1380], [1443, 1368], [1449, 1339], [1447, 1336], [1452, 1316], [1467, 1296], [1464, 1312], [1470, 1318], [1475, 1306], [1490, 1295], [1488, 1312], [1496, 1300], [1498, 1290], [1493, 1271], [1493, 1260], [1515, 1230], [1528, 1228], [1539, 1200], [1548, 1193], [1555, 1171], [1564, 1167], [1562, 1147], [1571, 1138], [1564, 1124], [1564, 1090], [1557, 1085], [1566, 1060], [1571, 1074], [1571, 1076], [1571, 1076], [1571, 1076], [1575, 1073], [1567, 1051], [1568, 1041], [1552, 1035]]], [[[1043, 1594], [1047, 1602], [1060, 1605], [1053, 1593], [1043, 1594], [1043, 1594], [1043, 1594]]], [[[1571, 1077], [1571, 1076], [1571, 1076], [1571, 1076], [1570, 1076], [1571, 1077], [1571, 1077]]], [[[1571, 1077], [1570, 1089], [1573, 1090], [1571, 1077], [1571, 1077]]], [[[1043, 1594], [1041, 1587], [1042, 1594], [1043, 1594], [1043, 1594], [1043, 1594]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.TAS",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.81,
                    "hc-middle-y": 0.65,
                    "hc-key": "au-ts",
                    "hc-a2": "TS",
                    "labelrank": "2",
                    "hasc": "AU.TS",
                    "alt-name": null,
                    "woe-id": "2344704",
                    "subregion": null,
                    "fips": "AS06",
                    "postal-code": "TS",
                    "name": "Tasmania",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "146.603",
                    "woe-name": null,
                    "latitude": "-42.1383",
                    "woe-label": null,
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[1307, 532], [1300, 538], [1311, 540], [1315, 534], [1307, 532]]], [[[1301, 568], [1311, 555], [1310, 543], [1304, 542], [1293, 561], [1301, 568]]], [[[1167, 565], [1162, 562], [1161, 581], [1169, 584], [1169, 568], [1167, 565]]], [[[1188, 539], [1187, 536], [1200, 528], [1219, 521], [1222, 517], [1242, 509], [1263, 515], [1277, 512], [1283, 519], [1291, 516], [1297, 522], [1307, 509], [1301, 474], [1303, 459], [1296, 463], [1290, 452], [1286, 437], [1284, 411], [1278, 411], [1274, 421], [1281, 419], [1281, 426], [1271, 429], [1272, 424], [1261, 419], [1263, 406], [1255, 411], [1245, 396], [1236, 402], [1225, 405], [1219, 403], [1217, 419], [1213, 415], [1210, 427], [1204, 431], [1198, 448], [1197, 466], [1204, 458], [1198, 477], [1190, 489], [1182, 522], [1188, 539]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.NSW",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.64,
                    "hc-middle-y": 0.29,
                    "hc-key": "au-ns",
                    "hc-a2": "NS",
                    "labelrank": "2",
                    "hasc": "AU.NS",
                    "alt-name": null,
                    "woe-id": "2344700",
                    "subregion": null,
                    "fips": "AS02",
                    "postal-code": "NS",
                    "name": "New South Wales",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "146.781",
                    "woe-name": "New South Wales",
                    "latitude": "-32.4751",
                    "woe-label": "New South Wales, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1418, 754], [1398, 726], [1395, 707], [1383, 682], [1386, 666], [1382, 656], [1324, 694], [1325, 701], [1320, 728], [1311, 734], [1300, 734], [1297, 729], [1279, 730], [1272, 736], [1263, 734], [1241, 740], [1235, 746], [1215, 747], [1214, 737], [1205, 736], [1194, 746], [1191, 754], [1160, 781], [1160, 798], [1138, 808], [1131, 801], [1126, 812], [1127, 820], [1121, 827], [1104, 832], [1097, 828], [1077, 838], [1089, 1055], [1391, 1029], [1413, 1044], [1433, 1041], [1444, 1042], [1449, 1037], [1462, 1036], [1479, 1021], [1480, 1010], [1489, 1018], [1507, 1019], [1507, 1034], [1529, 1043], [1538, 1037], [1552, 1035], [1568, 1041], [1570, 1036], [1567, 1010], [1554, 992], [1551, 976], [1531, 934], [1534, 926], [1525, 902], [1505, 875], [1504, 863], [1486, 850], [1476, 848], [1453, 819], [1449, 804], [1434, 790], [1428, 771], [1427, 761], [1421, 757], [1419, 756], [1418, 754]], [[1373, 753], [1364, 763], [1354, 757], [1350, 740], [1355, 731], [1361, 733], [1361, 743], [1366, 752], [1373, 753]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.VIC",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.58,
                    "hc-middle-y": 0.72,
                    "hc-key": "au-vi",
                    "hc-a2": "VI",
                    "labelrank": "2",
                    "hasc": "AU.VI",
                    "alt-name": null,
                    "woe-id": "2344705",
                    "subregion": null,
                    "fips": "AS07",
                    "postal-code": "VI",
                    "name": "Victoria",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "144.75",
                    "woe-name": "Victoria",
                    "latitude": "-37.0082",
                    "woe-label": "Victoria, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1066, 661], [1076, 839], [1077, 838], [1097, 828], [1104, 832], [1121, 827], [1127, 820], [1126, 812], [1131, 801], [1138, 808], [1160, 798], [1160, 781], [1191, 754], [1194, 746], [1205, 736], [1214, 737], [1215, 747], [1235, 746], [1241, 740], [1263, 734], [1272, 736], [1279, 730], [1297, 729], [1300, 734], [1311, 734], [1320, 728], [1325, 701], [1324, 694], [1382, 656], [1375, 655], [1363, 646], [1322, 649], [1308, 647], [1296, 641], [1269, 619], [1245, 616], [1248, 609], [1255, 614], [1251, 598], [1241, 614], [1235, 610], [1232, 622], [1209, 629], [1215, 635], [1216, 642], [1203, 631], [1198, 637], [1205, 638], [1210, 646], [1206, 658], [1201, 658], [1190, 650], [1197, 647], [1194, 642], [1185, 642], [1172, 635], [1164, 626], [1153, 619], [1137, 631], [1129, 634], [1115, 644], [1109, 643], [1093, 650], [1089, 644], [1079, 645], [1080, 650], [1066, 661]]]
                  }
                }
                ]
              },
              joinBy: 'hc-key',
              dataLabels: {
                enabled: true,
                style: {
                  textShadow: 'none',
                  color: '#fff',
                  fontSize: '1.3em',
                  fontFamily: 'ff-enzo-web'
                },
                formatter () {
                  return this.point.value + '%'
                }
              }
            }, {
              type: 'mapbubble',
              color: 'rgba(67,90,204,0.5)',
              enableMouseTracking: true,
              data: report.attendance,
              name: 'Attendance',
              mapData: {
                "title": "Australia",
                "version": "1.0.2",
                "type": "FeatureCollection",
                "copyright": "Copyright (c) 2014 Highsoft AS, Based on data from Natural Earth http://www.naturalearthdata.com",
                "copyrightShort": "Natural Earth",
                "copyrightUrl": "http://www.naturalearthdata.com",
                "crs": {
                  "type": "name",
                  "properties": {
                    "name": "urn:ogc:def:crs:EPSG:3112"
                  }
                },
                "features": [{
                  "type": "Feature",
                  "id": "AU.NT",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.45,
                    "hc-middle-y": 0.57,
                    "hc-key": "au-nt",
                    "hc-a2": "NT",
                    "labelrank": "2",
                    "hasc": "AU.NT",
                    "alt-name": null,
                    "woe-id": "2344701",
                    "subregion": null,
                    "fips": "AS03",
                    "postal-code": "NT",
                    "name": "Northern Territory",
                    "country": "Australia",
                    "type-en": "Territory",
                    "region": null,
                    "longitude": "133.78",
                    "woe-name": "Northern Territory",
                    "latitude": "-20.1026",
                    "woe-label": "Northern Territory, AU, Australia",
                    "type": "Territory"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[932, 1724], [936, 1727], [944, 1721], [940, 1711], [951, 1704], [948, 1701], [928, 1706], [928, 1723], [932, 1724]]], [[[663, 1826], [666, 1816], [674, 1812], [649, 1810], [655, 1818], [655, 1825], [661, 1833], [663, 1826]]], [[[668, 1835], [678, 1830], [691, 1832], [700, 1839], [709, 1837], [714, 1827], [704, 1816], [690, 1806], [668, 1818], [662, 1837], [668, 1835]]], [[[992, 1601], [992, 1601], [992, 1600], [979, 1190], [627, 1188], [608, 1673], [608, 1673], [620, 1663], [618, 1675], [629, 1671], [637, 1662], [630, 1677], [643, 1675], [633, 1687], [623, 1694], [630, 1709], [638, 1712], [644, 1738], [655, 1737], [660, 1745], [654, 1751], [654, 1760], [662, 1762], [664, 1774], [672, 1775], [675, 1783], [694, 1787], [692, 1798], [713, 1790], [731, 1794], [737, 1790], [765, 1798], [762, 1820], [751, 1830], [739, 1825], [731, 1834], [724, 1835], [734, 1843], [744, 1831], [743, 1843], [750, 1844], [759, 1835], [755, 1848], [759, 1850], [764, 1826], [773, 1835], [787, 1817], [805, 1814], [814, 1818], [820, 1809], [826, 1811], [842, 1802], [857, 1807], [870, 1793], [883, 1801], [893, 1800], [893, 1806], [910, 1811], [895, 1797], [899, 1790], [906, 1798], [913, 1794], [912, 1784], [922, 1784], [927, 1792], [916, 1801], [931, 1806], [941, 1791], [946, 1797], [951, 1788], [932, 1766], [938, 1761], [931, 1749], [926, 1756], [917, 1748], [905, 1745], [905, 1727], [912, 1731], [920, 1722], [910, 1723], [905, 1707], [899, 1704], [882, 1684], [886, 1674], [919, 1652], [929, 1640], [934, 1648], [938, 1630], [950, 1632], [966, 1620], [982, 1614], [992, 1601]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.WA",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.47,
                    "hc-middle-y": 0.54,
                    "hc-key": "au-wa",
                    "hc-a2": "WA",
                    "labelrank": "2",
                    "hasc": "AU.WA",
                    "alt-name": null,
                    "woe-id": "2344706",
                    "subregion": null,
                    "fips": "AS08",
                    "postal-code": "WA",
                    "name": "Western Australia",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "121.646",
                    "woe-name": "Western Australia",
                    "latitude": "-25.8483",
                    "woe-label": "Western Australia, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[0, 1146], [14, 1124], [7, 1127], [0, 1139], [0, 1146]]], [[[608, 1673], [608, 1673], [627, 1188], [637, 958], [637, 942], [602, 924], [575, 914], [553, 911], [534, 913], [523, 908], [511, 898], [492, 889], [482, 881], [469, 877], [461, 869], [457, 850], [443, 832], [429, 828], [426, 834], [399, 829], [390, 825], [387, 832], [380, 829], [370, 831], [335, 824], [330, 820], [315, 820], [301, 809], [291, 791], [276, 793], [274, 786], [264, 780], [262, 771], [238, 762], [236, 757], [226, 761], [200, 756], [176, 762], [160, 777], [149, 782], [142, 778], [135, 790], [132, 814], [143, 810], [148, 813], [155, 828], [148, 854], [152, 860], [149, 887], [145, 898], [121, 931], [109, 956], [105, 987], [97, 1003], [85, 1015], [82, 1026], [65, 1041], [61, 1059], [54, 1074], [33, 1099], [20, 1109], [22, 1119], [32, 1103], [41, 1104], [42, 1115], [34, 1119], [20, 1138], [24, 1144], [34, 1133], [38, 1120], [41, 1131], [45, 1116], [51, 1112], [53, 1139], [49, 1140], [27, 1166], [20, 1182], [12, 1189], [8, 1210], [12, 1229], [17, 1235], [17, 1255], [7, 1274], [16, 1307], [22, 1311], [21, 1294], [28, 1280], [35, 1283], [42, 1312], [72, 1331], [99, 1365], [107, 1367], [121, 1381], [125, 1374], [135, 1380], [144, 1376], [159, 1381], [174, 1396], [201, 1404], [206, 1413], [215, 1418], [230, 1415], [240, 1421], [257, 1426], [285, 1441], [294, 1449], [306, 1467], [316, 1495], [320, 1495], [335, 1511], [336, 1517], [328, 1517], [325, 1549], [340, 1571], [351, 1572], [348, 1580], [353, 1589], [377, 1550], [384, 1543], [387, 1558], [384, 1566], [396, 1557], [387, 1578], [375, 1587], [387, 1591], [379, 1606], [389, 1604], [387, 1598], [399, 1594], [395, 1601], [414, 1594], [432, 1596], [416, 1598], [423, 1615], [415, 1617], [413, 1633], [423, 1634], [437, 1659], [452, 1652], [456, 1658], [442, 1669], [441, 1681], [446, 1676], [461, 1680], [461, 1694], [465, 1682], [470, 1685], [475, 1676], [480, 1682], [483, 1701], [487, 1696], [496, 1707], [504, 1696], [509, 1716], [518, 1720], [526, 1709], [535, 1712], [549, 1701], [559, 1689], [574, 1679], [569, 1660], [582, 1675], [588, 1677], [608, 1673]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.SA",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.34,
                    "hc-middle-y": 0.26,
                    "hc-key": "au-sa",
                    "hc-a2": "SA",
                    "labelrank": "2",
                    "hasc": "AU.SA",
                    "alt-name": null,
                    "woe-id": "2344703",
                    "subregion": null,
                    "fips": "AS05",
                    "postal-code": "SA",
                    "name": "South Australia",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "135.783",
                    "woe-name": "South Australia",
                    "latitude": "-29.6504",
                    "woe-label": "South Australia, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[953, 774], [951, 768], [968, 766], [967, 759], [957, 762], [946, 753], [937, 757], [920, 754], [913, 761], [915, 767], [943, 775], [953, 774]]], [[[637, 942], [637, 958], [627, 1188], [979, 1190], [1096, 1185], [1089, 1055], [1077, 838], [1076, 839], [1066, 661], [1057, 661], [1045, 670], [1042, 679], [1033, 688], [1025, 701], [1023, 712], [1030, 719], [1030, 726], [1021, 750], [1003, 772], [1007, 776], [1010, 767], [1015, 777], [1012, 783], [1001, 780], [1002, 774], [993, 775], [985, 771], [973, 770], [970, 775], [982, 784], [986, 810], [977, 824], [971, 837], [955, 792], [945, 795], [937, 790], [925, 790], [932, 805], [947, 804], [950, 817], [948, 837], [954, 849], [967, 864], [962, 876], [970, 880], [964, 906], [961, 887], [949, 881], [940, 859], [918, 849], [909, 841], [904, 831], [893, 822], [891, 811], [895, 802], [881, 807], [865, 823], [874, 817], [872, 833], [866, 850], [854, 861], [853, 874], [847, 879], [833, 882], [826, 901], [834, 903], [828, 913], [817, 909], [821, 916], [810, 928], [801, 923], [791, 924], [778, 934], [758, 931], [731, 949], [718, 954], [705, 948], [680, 949], [637, 942]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.ACT",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.17,
                    "hc-middle-y": 0.18,
                    "hc-key": "au-ct",
                    "hc-a2": "CT",
                    "labelrank": "9",
                    "hasc": "AU.CT",
                    "alt-name": null,
                    "woe-id": "1100968",
                    "subregion": null,
                    "fips": "AS01",
                    "postal-code": "CT",
                    "name": "Australian Capital Territory",
                    "country": "Australia",
                    "type-en": "Territory",
                    "region": null,
                    "longitude": "148.983",
                    "woe-name": "Canberra",
                    "latitude": "-35.4618",
                    "woe-label": null,
                    "type": "Territory"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1373, 753], [1366, 752], [1361, 743], [1361, 733], [1355, 731], [1350, 740], [1354, 757], [1364, 763], [1373, 753]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.QLD",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.3,
                    "hc-middle-y": 0.6,
                    "hc-key": "au-ql",
                    "hc-a2": "QL",
                    "labelrank": "2",
                    "hasc": "AU.QL",
                    "alt-name": null,
                    "woe-id": "2344702",
                    "subregion": null,
                    "fips": "AS04",
                    "postal-code": "QL",
                    "name": "Queensland",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "144.778",
                    "woe-name": "Queensland",
                    "latitude": "-23.1364",
                    "woe-label": "Queensland, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[1582, 1189], [1584, 1176], [1566, 1145], [1564, 1156], [1570, 1171], [1579, 1180], [1576, 1186], [1582, 1189]]], [[[1502, 1257], [1508, 1252], [1507, 1242], [1499, 1255], [1502, 1257]]], [[[1552, 1035], [1538, 1037], [1529, 1043], [1507, 1034], [1507, 1019], [1489, 1018], [1480, 1010], [1479, 1021], [1462, 1036], [1449, 1037], [1444, 1042], [1433, 1041], [1413, 1044], [1391, 1029], [1089, 1055], [1096, 1185], [979, 1190], [992, 1600], [992, 1601], [1000, 1594], [1020, 1590], [1036, 1583], [1045, 1564], [1075, 1547], [1096, 1549], [1110, 1556], [1122, 1584], [1127, 1588], [1139, 1615], [1137, 1621], [1143, 1641], [1152, 1661], [1147, 1685], [1151, 1703], [1147, 1708], [1149, 1725], [1158, 1739], [1154, 1751], [1165, 1765], [1155, 1771], [1165, 1792], [1171, 1789], [1182, 1823], [1185, 1841], [1198, 1850], [1205, 1848], [1214, 1818], [1213, 1798], [1227, 1791], [1221, 1776], [1235, 1762], [1231, 1751], [1237, 1748], [1239, 1721], [1236, 1711], [1241, 1699], [1244, 1682], [1251, 1676], [1261, 1682], [1264, 1692], [1267, 1684], [1277, 1689], [1282, 1672], [1291, 1669], [1296, 1659], [1309, 1652], [1304, 1646], [1304, 1626], [1310, 1603], [1306, 1586], [1320, 1565], [1327, 1565], [1323, 1558], [1332, 1531], [1324, 1504], [1336, 1504], [1337, 1497], [1332, 1477], [1338, 1468], [1365, 1450], [1378, 1451], [1386, 1429], [1389, 1435], [1396, 1425], [1410, 1421], [1414, 1411], [1421, 1416], [1422, 1408], [1434, 1404], [1422, 1393], [1429, 1380], [1443, 1368], [1449, 1339], [1447, 1336], [1452, 1316], [1467, 1296], [1464, 1312], [1470, 1318], [1475, 1306], [1490, 1295], [1488, 1312], [1496, 1300], [1498, 1290], [1493, 1271], [1493, 1260], [1515, 1230], [1528, 1228], [1539, 1200], [1548, 1193], [1555, 1171], [1564, 1167], [1562, 1147], [1571, 1138], [1564, 1124], [1564, 1090], [1557, 1085], [1566, 1060], [1571, 1074], [1571, 1076], [1571, 1076], [1571, 1076], [1575, 1073], [1567, 1051], [1568, 1041], [1552, 1035]]], [[[1043, 1594], [1047, 1602], [1060, 1605], [1053, 1593], [1043, 1594], [1043, 1594], [1043, 1594]]], [[[1571, 1077], [1571, 1076], [1571, 1076], [1571, 1076], [1570, 1076], [1571, 1077], [1571, 1077]]], [[[1571, 1077], [1570, 1089], [1573, 1090], [1571, 1077], [1571, 1077]]], [[[1043, 1594], [1041, 1587], [1042, 1594], [1043, 1594], [1043, 1594], [1043, 1594]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.TAS",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.30,
                    "hc-middle-y": 0.65,
                    "hc-key": "au-ts",
                    "hc-a2": "TS",
                    "labelrank": "2",
                    "hasc": "AU.TS",
                    "alt-name": null,
                    "woe-id": "2344704",
                    "subregion": null,
                    "fips": "AS06",
                    "postal-code": "TS",
                    "name": "Tasmania",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "146.603",
                    "woe-name": null,
                    "latitude": "-42.1383",
                    "woe-label": null,
                    "type": "State"
                  },
                  "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [[[[1307, 532], [1300, 538], [1311, 540], [1315, 534], [1307, 532]]], [[[1301, 568], [1311, 555], [1310, 543], [1304, 542], [1293, 561], [1301, 568]]], [[[1167, 565], [1162, 562], [1161, 581], [1169, 584], [1169, 568], [1167, 565]]], [[[1188, 539], [1187, 536], [1200, 528], [1219, 521], [1222, 517], [1242, 509], [1263, 515], [1277, 512], [1283, 519], [1291, 516], [1297, 522], [1307, 509], [1301, 474], [1303, 459], [1296, 463], [1290, 452], [1286, 437], [1284, 411], [1278, 411], [1274, 421], [1281, 419], [1281, 426], [1271, 429], [1272, 424], [1261, 419], [1263, 406], [1255, 411], [1245, 396], [1236, 402], [1225, 405], [1219, 403], [1217, 419], [1213, 415], [1210, 427], [1204, 431], [1198, 448], [1197, 466], [1204, 458], [1198, 477], [1190, 489], [1182, 522], [1188, 539]]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.NSW",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.24,
                    "hc-middle-y": 0.29,
                    "hc-key": "au-ns",
                    "hc-a2": "NS",
                    "labelrank": "2",
                    "hasc": "AU.NS",
                    "alt-name": null,
                    "woe-id": "2344700",
                    "subregion": null,
                    "fips": "AS02",
                    "postal-code": "NS",
                    "name": "New South Wales",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "146.781",
                    "woe-name": "New South Wales",
                    "latitude": "-32.4751",
                    "woe-label": "New South Wales, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1418, 754], [1398, 726], [1395, 707], [1383, 682], [1386, 666], [1382, 656], [1324, 694], [1325, 701], [1320, 728], [1311, 734], [1300, 734], [1297, 729], [1279, 730], [1272, 736], [1263, 734], [1241, 740], [1235, 746], [1215, 747], [1214, 737], [1205, 736], [1194, 746], [1191, 754], [1160, 781], [1160, 798], [1138, 808], [1131, 801], [1126, 812], [1127, 820], [1121, 827], [1104, 832], [1097, 828], [1077, 838], [1089, 1055], [1391, 1029], [1413, 1044], [1433, 1041], [1444, 1042], [1449, 1037], [1462, 1036], [1479, 1021], [1480, 1010], [1489, 1018], [1507, 1019], [1507, 1034], [1529, 1043], [1538, 1037], [1552, 1035], [1568, 1041], [1570, 1036], [1567, 1010], [1554, 992], [1551, 976], [1531, 934], [1534, 926], [1525, 902], [1505, 875], [1504, 863], [1486, 850], [1476, 848], [1453, 819], [1449, 804], [1434, 790], [1428, 771], [1427, 761], [1421, 757], [1419, 756], [1418, 754]], [[1373, 753], [1364, 763], [1354, 757], [1350, 740], [1355, 731], [1361, 733], [1361, 743], [1366, 752], [1373, 753]]]
                  }
                }, {
                  "type": "Feature",
                  "id": "AU.VIC",
                  "properties": {
                    "hc-group": "admin1",
                    "hc-middle-x": 0.08,
                    "hc-middle-y": 0.72,
                    "hc-key": "au-vi",
                    "hc-a2": "VI",
                    "labelrank": "2",
                    "hasc": "AU.VI",
                    "alt-name": null,
                    "woe-id": "2344705",
                    "subregion": null,
                    "fips": "AS07",
                    "postal-code": "VI",
                    "name": "Victoria",
                    "country": "Australia",
                    "type-en": "State",
                    "region": null,
                    "longitude": "144.75",
                    "woe-name": "Victoria",
                    "latitude": "-37.0082",
                    "woe-label": "Victoria, AU, Australia",
                    "type": "State"
                  },
                  "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[1066, 661], [1076, 839], [1077, 838], [1097, 828], [1104, 832], [1121, 827], [1127, 820], [1126, 812], [1131, 801], [1138, 808], [1160, 798], [1160, 781], [1191, 754], [1194, 746], [1205, 736], [1214, 737], [1215, 747], [1235, 746], [1241, 740], [1263, 734], [1272, 736], [1279, 730], [1297, 729], [1300, 734], [1311, 734], [1320, 728], [1325, 701], [1324, 694], [1382, 656], [1375, 655], [1363, 646], [1322, 649], [1308, 647], [1296, 641], [1269, 619], [1245, 616], [1248, 609], [1255, 614], [1251, 598], [1241, 614], [1235, 610], [1232, 622], [1209, 629], [1215, 635], [1216, 642], [1203, 631], [1198, 637], [1205, 638], [1210, 646], [1206, 658], [1201, 658], [1190, 650], [1197, 647], [1194, 642], [1185, 642], [1172, 635], [1164, 626], [1153, 619], [1137, 631], [1129, 634], [1115, 644], [1109, 643], [1093, 650], [1089, 644], [1079, 645], [1080, 650], [1066, 661]]]
                  }
                }
                ]
              },
              joinBy: ['hc-key'],
              dataLabels: {
                enabled: true,
                style: {
                  textShadow: 'none',
                  color: '#fff',
                  fontSize: '1.3em',
                  fontFamily: 'ff-enzo-web'
                },
                formatter () {
                  return this.point.value + '%'
                }
              }
            }]
          }
        }
      }

      return false
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.chart-wrapper {
  position: relative;
  .nb {
    position: absolute;
    bottom: -10px;
    right: -60px;
    color: #fff;
    font-style: italic;
    font-size: 12px;
  }
}
</style>
